import { useEffect, useRef } from 'react';
import { useSWRConfig } from 'swr';
import { useConfig } from '../config';
import { Res, useApi } from '../state/api';

declare global {
	interface WindowEventMap {
		/**
		 * Dispatched by the chat widget, cancelable, once it has signed the customer in through
		 * kg-api. Calling preventDefault tells the widget the page has taken the sign-in in; if
		 * nobody does, the widget reloads the page.
		 */
		'chatwidget:signed-in': Event;
	}
}

/*
 * The chat widget's sign-in starts the same kg-api session as the site's own login. The site
 * reloads after its login; doing that here would interrupt the chat the customer is in, so this
 * takes the sign-in in place instead: it revalidates every kg-api key, which refetches the mounted
 * ones (session, cart, prices, favourites ...) and keeps their data meanwhile, so nothing flickers.
 *
 * Only on a page that has never shown a customer. State built for one customer is not all undone
 * by a revalidation (the special prices index keeps the last non-empty list, for one), so once a
 * customer was seen - even one signed out since - the event is left alone and the widget reloads.
 *
 * Unlike after the site's login, whose reload empties the cache, keys that are not mounted keep
 * what they hold: with revalidateIfStale off (manualcontrol), a key with cached data does not
 * refetch when it mounts again either, only when it is next invalidated. Accepted, since with the
 * rule above that cache can only be a guest's.
 *
 * Renders nothing.
 */
export const ChatSignInRefresh = (): null => {
	const { api } = useConfig();
	const { mutate, cache } = useSWRConfig();
	const { data: session } = useApi(Res.session, {});

	const customerSeen = useRef(false);
	useEffect(() => {
		if (typeof session === 'object' && session !== null) customerSeen.current = true;
	}, [session]);

	useEffect(() => {
		const sessionKey = api + Res.session.uri({});

		const refresh = async () => {
			// everything else is refetched without waiting for it: a hung request must not hold
			// up the decision below
			mutate((key) => typeof key === 'string' && key.startsWith(api) && key !== sessionKey).catch(() =>
				window.location.reload()
			);
			await mutate(sessionKey);
			// mutate resolves even when the refetch fails, leaving the error in the cache. without
			// the session the page still shows a guest, so that failure is worth a reload
			if (cache.get(sessionKey)?.error) window.location.reload();
		};

		const onSignedIn = (event: Event) => {
			if (customerSeen.current) return;
			// acknowledged first: from here on the widget will not reload, so a refresh that fails
			// or leaves the session unknown reloads here
			event.preventDefault();
			refresh().catch(() => window.location.reload());
		};
		window.addEventListener('chatwidget:signed-in', onSignedIn);
		return () => window.removeEventListener('chatwidget:signed-in', onSignedIn);
	}, [api, mutate, cache]);

	return null;
};
